<template>
  <div class="entra-page">
    <EntraPageHeader
      title="Activity"
      :tenantDisplayName="tenantName"
      :primaryDomain="primaryDomain"
      :regionCode="regionCode"
      :tenantStatus="tenantStatus"
      :freshnessState="freshnessState"
      :lastCollection="lastCollection"
      :dataCompleteness="dataCompleteness"
      :capabilities="capabilities"
      @refresh="loadData"
      @export="handleExport"
    />

    <div class="page-body">
      <div class="filters-bar">
        <select v-model="dateRange" @change="applyFilters" class="filter-select">
          <option value="24h">Last 24 hours</option>
          <option value="7d">Last 7 days</option>
          <option value="30d">Last 30 days</option>
          <option value="90d">Last 90 days</option>
        </select>
        <select v-model="category" @change="applyFilters" class="filter-select">
          <option value="">All Categories</option>
          <option value="ingestion">Ingestion</option>
          <option value="collection">Collection Jobs</option>
          <option value="finding">Findings</option>
        </select>
      </div>

      <div v-if="error" class="error-message">{{ error }}</div>

      <div v-if="tableState === 'loading'" class="loading-state">Loading activity...</div>
      <div v-else-if="tableState === 'empty'" class="empty-wrapper">
        <p>No activity found for the selected time range.</p>
      </div>
      <div v-else class="table-wrapper">
        <table class="data-table">
          <thead>
            <tr>
              <th>Timestamp</th>
              <th>Category</th>
              <th>Activity</th>
              <th>Target</th>
              <th>Actor</th>
              <th>Source</th>
              <th>Result</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="event in events" :key="event.id">
              <td class="timestamp">{{ formatTimestamp(event.timestamp) }}</td>
              <td><span class="category-badge">{{ event.category }}</span></td>
              <td>{{ event.activity }}</td>
              <td class="target">{{ event.target || '--' }}</td>
              <td>{{ event.actor || '--' }}</td>
              <td>{{ event.source }}</td>
              <td><span class="result-badge" :class="'result-' + event.result">{{ event.result }}</span></td>
            </tr>
          </tbody>
        </table>

        <div class="pagination" v-if="totalPages > 1">
          <button class="page-btn" :disabled="page <= 1" @click="goToPage(page - 1)">Previous</button>
          <span class="page-info">Page {{ page }} of {{ totalPages }}</span>
          <button class="page-btn" :disabled="page >= totalPages" @click="goToPage(page + 1)">Next</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import EntraPageHeader from '@/components/entra/common/EntraPageHeader.vue';
import { getEntraActivity, getEntraTenantSummary, getEntraTenantCapabilities } from '@/services/entra-api.js';
import { getCapabilityStateLabel } from '@/services/entra-utils.js';

export default {
  name: 'EntraActivity',
  components: { EntraPageHeader },
  data: function() {
    return {
      tenantId: '', tenantName: '', primaryDomain: '', regionCode: '', tenantStatus: '',
      freshnessState: 'fresh', lastCollection: '', dataCompleteness: null, capabilities: [],
      dateRange: '7d',
      category: '',
      events: [], tableState: 'loading',
      page: 1, pageSize: 50, totalPages: 1,
      error: ''
    };
  },
  methods: {
    formatTimestamp: function(ts) {
      if (!ts) return '--';
      return new Date(ts).toLocaleString();
    },
    loadTenantMeta: function() {
      var self = this;
      getEntraTenantSummary(this.tenantId).then(function(data) {
        var t = data.tenant || data;
        self.tenantName = t.displayName || '';
        self.primaryDomain = t.primaryDomain || '';
        self.regionCode = t.regionCode || '';
        self.tenantStatus = t.status || '';
        self.lastCollection = t.lastSuccessfulCollectionAt || '';
        self.dataCompleteness = t.dataCompleteness || null;
      }).catch(function() {});
      getEntraTenantCapabilities(this.tenantId).then(function(data) {
        self.capabilities = (data || []).map(function(c) {
          return { key: c.capabilityKey, state: c.state, stateLabel: getCapabilityStateLabel(c.state) };
        });
      }).catch(function() {});
    },
    applyFilters: function() { this.page = 1; this.loadActivity(); },
    goToPage: function(p) { this.page = p; this.loadActivity(); },
    handleExport: function() {},
    loadActivity: function() {
      var self = this;
      getEntraActivity(this.tenantId, {
        page: this.page,
        pageSize: this.pageSize,
        dateRange: this.dateRange,
        category: this.category || undefined
      }).then(function(data) {
        self.events = data.items || [];
        self.totalPages = Math.max(1, Math.ceil((data.total || 0) / self.pageSize));
        self.tableState = self.events.length === 0 ? 'empty' : 'loaded';
      }).catch(function(err) {
        self.error = err.message || 'Failed to load activity.';
        self.tableState = 'error';
      });
    },
    loadData: function() {
      this.tenantId = this.$route.params.tenantId || '';
      if (!this.tenantId) {
        this.error = 'Please select a tenant from the Portfolio page first.';
        this.tableState = 'empty';
        return;
      }
      this.loadTenantMeta();
      this.loadActivity();
    }
  },
  mounted: function() { this.loadData(); },
  watch: { '$route.params.tenantId': 'loadData' }
};
</script>

<style scoped>
.entra-page { min-height: 100%; }
.page-body { padding: 24px; }
.filters-bar { display: flex; gap: 12px; margin-bottom: 16px; }
.filter-select { padding: 6px 12px; border: 1px solid #e2e8f0; border-radius: 6px; font-size: 0.8125rem; color: #334155; background: #ffffff; font-family: 'Inter', sans-serif; }
.error-message { padding: 16px; background: #fef2f2; border: 1px solid #fecaca; border-radius: 8px; color: #991b1b; font-size: 0.875rem; }
.loading-state { padding: 48px; text-align: center; color: #94a3b8; }
.empty-wrapper { padding: 48px; text-align: center; color: #94a3b8; font-size: 0.875rem; }
.table-wrapper { background: #ffffff; border: 1px solid #e2e8f0; border-radius: 8px; overflow: hidden; }
.data-table { width: 100%; border-collapse: collapse; font-size: 0.8125rem; }
.data-table th { background: #f8fafc; color: #475569; font-weight: 600; text-align: left; padding: 10px 12px; border-bottom: 2px solid #e2e8f0; white-space: nowrap; }
.data-table td { padding: 10px 12px; border-bottom: 1px solid #f1f5f9; color: #334155; }
.timestamp { font-family: monospace; font-size: 0.75rem; color: #64748b; white-space: nowrap; }
.target { font-family: monospace; font-size: 0.6875rem; color: #64748b; }
.category-badge { font-size: 0.6875rem; padding: 1px 6px; border-radius: 3px; background: #f1f5f9; color: #475569; text-transform: uppercase; font-weight: 600; }
.result-badge { font-size: 0.6875rem; padding: 1px 6px; border-radius: 3px; font-weight: 600; text-transform: uppercase; }
.result-success { background: #dcfce7; color: #166534; }
.result-failure { background: #fee2e2; color: #991b1b; }
.result-pending { background: #fef3c7; color: #92400e; }
.pagination { display: flex; align-items: center; justify-content: center; gap: 12px; padding: 12px; border-top: 1px solid #e2e8f0; }
.page-btn { background: transparent; border: 1px solid #cbd5e1; padding: 6px 14px; border-radius: 6px; font-size: 0.8125rem; color: #475569; cursor: pointer; font-family: 'Inter', sans-serif; }
.page-btn:hover:not(:disabled) { background: #f1f5f9; }
.page-btn:disabled { opacity: 0.4; cursor: not-allowed; }
.page-info { font-size: 0.8125rem; color: #64748b; }
</style>
